import React, { useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import './AdminLogin.css';

const jsonRequest = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Client-Platform': 'manager',
      'X-Client-Version': '0.2.0',
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || !payload || payload.success === false) {
    throw new Error((payload && payload.message) || 'Unable to sign in.');
  }
  return payload;
};

export default function AdminLogin({ serverUrl, onAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [requestId, setRequestId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = requestId
        ? await jsonRequest(`${serverUrl}/admin/login/verify`, {
          requestId,
          code,
          keepSignedIn: true,
        })
        : await jsonRequest(`${serverUrl}/admin/login`, {
          email,
          password,
          keepSignedIn: true,
        });
      if (payload.verificationRequired) {
        setRequestId(payload.requestId);
        setCode('');
      } else if (payload.token) {
        onAuthenticated(payload);
      } else {
        throw new Error('The sign-in response did not include a session token.');
      }
    } catch (requestError) {
      setError(requestError.message || 'Unable to sign in.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="tm-login-shell">
      <div className="tm-login-glow tm-login-glow--one" aria-hidden="true" />
      <div className="tm-login-glow tm-login-glow--two" aria-hidden="true" />
      <div className="tm-login-brand">
        <span className="tm-login-mark">E</span>
        <span><strong>Evergreen</strong><small>Host Console</small></span>
      </div>

      <Container className="tm-login-container">
        <section className="tm-login-card">
          <div className="tm-login-card__accent" />
          <div className="tm-login-kicker"><i />Secure front desk access</div>
          <h2>Table Management Sign In</h2>
          <p className="tm-login-intro">
            {requestId
              ? 'Enter the verification code sent to your administrator email.'
              : 'Welcome back. Sign in once and this trusted front-desk device will stay connected.'}
          </p>
          {error ? <Alert variant="danger" className="tm-login-alert">{error}</Alert> : null}
          <Form onSubmit={submit} className="tm-login-form">
            {!requestId ? (
              <>
                <Form.Group>
                  <Form.Label htmlFor="table-admin-email">Email address</Form.Label>
                  <Form.Control id="table-admin-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="manager@evergreen.com" autoComplete="username" required />
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="table-admin-password">Password</Form.Label>
                  <Form.Control id="table-admin-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter your password" autoComplete="current-password" required />
                </Form.Group>
              </>
            ) : (
              <Form.Group>
                <Form.Label htmlFor="table-admin-code">Verification code</Form.Label>
                <Form.Control className="tm-code-input" id="table-admin-code" value={code} onChange={(event) => setCode(event.target.value)} inputMode="numeric" autoComplete="one-time-code" placeholder="000000" required autoFocus />
              </Form.Group>
            )}
            <Button className="tm-login-submit" type="submit" disabled={submitting} aria-label={requestId ? 'Verify' : 'Sign in'}>
              <span>{submitting ? 'Signing in…' : (requestId ? 'Verify device' : 'Sign in securely')}</span>
              <span aria-hidden="true">→</span>
            </Button>
          </Form>
          <div className="tm-login-persistence">
            <span className="tm-login-shield" aria-hidden="true">✓</span>
            <span><strong>Persistent secure session</strong><small>Automatic renewal on this device. You can sign out at any time.</small></span>
          </div>
        </section>
        <p className="tm-login-footer">Evergreen Restaurant · Front Desk Operations</p>
      </Container>
    </main>
  );
}
